import { useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import dayjs from 'dayjs'

import LinkOutlinedIcon from '@ant-design/icons/LinkOutlined'

import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import DialogTitle from '@components/_mui/DialogTitle'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import usePageParams from '@shared/hooks/src/usePageParams'
import { isUserAllowed, RoleGuard, useMe } from '@shared/providers/src/MeProvider'
import { LabStatus, LabTypes, UserRole } from '@shared/utils'

import LinearProgress from '@components/LinearProgress'
import Pagination from '@components/Pagination'
import RenderControl from '@components/RenderControl'
import StatusChip from '@components/StatusChip'
import TableSortCell from '@components/TableSortCell'

import LabDetailsModal from '../Lab/LabDetailsModal'
import { SelectedUnmatchedLab } from '../Labs.utils'
import { usePatientLabs, usePatientLabsFilters } from '../PatientLabs/PatientLabs.hooks'
import UnmatchedLabs from '../UnmatchedLabs'
import { useUnmatchedLabMatchDialog } from '../UnmatchedLabs/UnmatchedLabs.hooks'

export default function LabsModal({ patientId, open, onClose }) {
  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose} scroll="body">
      <Content patientId={patientId} onClose={onClose} />
    </Dialog>
  )
}

function Content({ patientId, onClose }) {
  const [labMatchFlow, setLabMatchFlow] = useState(false)
  const [unmatchedLab, setUnmatchedLab] = useState(null)
  const [selectedLabId, setSelectedLabId] = useState(null)

  const me = useMe()
  const [{ labsTab, labsOrder, labsSort }, updateFilters] = usePatientLabsFilters()
  const [page, perPage, setPageParams] = usePageParams({ id: 'labs' })

  const { data, isRefreshing, isPending } = usePatientLabs(
    patientId,
    {
      filter: labsTab,
      order: labsOrder,
      sort: labsSort,
      limit: perPage,
      offset: (page - 1) * perPage,
    },
    { placeholderData: keepPreviousData }
  )

  const onLabMatch = useUnmatchedLabMatchDialog({
    onMatchSuccess: () => setLabMatchFlow(false),
  })

  return (
    <>
      <LabDetailsModal open={Boolean(selectedLabId)} labId={selectedLabId} onClose={() => setSelectedLabId(null)} />
      <LinearProgress loading={isRefreshing} />
      <DialogTitle onClose={onClose}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Typography component="h2" variant="h4">
            Labs
          </Typography>
          <RoleGuard allowed={[UserRole.Admin, UserRole.MA]}>
            <UnmatchedLabs
              onNotApplicable={(unmatchedLabId) => {
                if (unmatchedLabId === unmatchedLab?.id) {
                  setLabMatchFlow(false)
                }
              }}
              onSelect={(lab) => {
                setUnmatchedLab(lab)
                setLabMatchFlow(true)
              }}
            />
          </RoleGuard>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <RoleGuard allowed={[UserRole.Admin]}>
          <Box sx={{ px: 2 }}>
            <SelectedUnmatchedLab open={labMatchFlow} lab={unmatchedLab} onClose={() => setLabMatchFlow(false)} />
          </Box>
        </RoleGuard>
        <Tabs
          value={labsTab}
          onChange={(e, labsTab) => {
            updateFilters({ labsTab })
            setPageParams({ page: 1, perPage })
          }}
        >
          <Tab label="All" value="all" />
          <Tab label="Complete" value="complete" />
          <Tab label="Pending" value="pending" />
          <Tab label="Follow-up" value="follow-up" />
          <Tab label="Failed" value="failed" />
        </Tabs>
        <RenderControl loading={isPending} isEmpty={data?.length === 0} emptyTitle="No data to display">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableSortCell
                    active={labsSort === 'lab'}
                    direction={labsOrder}
                    onChange={(labsOrder) => {
                      updateFilters({ labsOrder, labsSort: 'lab' })
                      setPageParams({ page: 1, perPage })
                    }}
                  >
                    Type
                  </TableSortCell>
                  <TableCell>Labwork</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableSortCell
                    active={labsSort === 'result_date'}
                    direction={labsOrder}
                    onChange={(labsOrder) => {
                      updateFilters({ labsOrder, labsSort: 'result_date' })
                      setPageParams({ page: 1, perPage })
                    }}
                  >
                    Result
                  </TableSortCell>
                  <TableSortCell
                    active={labsSort === 'created_at'}
                    direction={labsOrder}
                    onChange={(labsOrder) => {
                      updateFilters({ labsOrder, labsSort: 'created_at' })
                      setPageParams({ page: 1, perPage })
                    }}
                  >
                    Created
                  </TableSortCell>
                  <RoleGuard allowed={[UserRole.Admin]}>
                    <TableCell />
                  </RoleGuard>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map((lab) => {
                  const canMatch =
                    labMatchFlow &&
                    isUserAllowed(me, [UserRole.Admin]) &&
                    lab.lab === LabTypes.Quest &&
                    !lab.hasResultFiles &&
                    ![LabStatus.Released, LabStatus.NeedsReview, LabStatus.NurseReviewed, LabStatus.NeedsFollowUp].includes(lab.status)

                  return (
                    <Lab
                      key={lab.id}
                      data={lab}
                      canMatch={canMatch}
                      onMatch={() => onLabMatch({ unmatchedLab, relateLab: lab })}
                      onSelect={() => setSelectedLabId(lab.id)}
                    />
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </RenderControl>
      </DialogContent>
      <DialogActions sx={{ width: '100%', justifyContent: 'flex-start' }}>
        <Pagination id="labs" loading={isPending} last={data?.length < perPage} />
      </DialogActions>
    </>
  )
}

function Lab({ data, canMatch, onMatch, onSelect }) {
  return (
    <TableRow hover onClick={onSelect} sx={{ cursor: 'pointer' }}>
      <TableCell>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
          <Typography>{data.lab}</Typography>
          <StatusChip.Lab status={data.status} noLabel />
        </Stack>
      </TableCell>
      <TableCell>{data.panelsToString}</TableCell>
      <TableCell>{data.provider?.name}</TableCell>
      <TableCell>{data.resultDate ? dayjs(data.resultDate).format('L') : ''}</TableCell>
      <TableCell>{dayjs(data.createdAt).format('L')}</TableCell>
      <RoleGuard allowed={[UserRole.Admin]}>
        <TableCell align="right">
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            {canMatch && (
              <IconButton
                size="small"
                shape="rounded"
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation()
                  onMatch()
                }}
              >
                <LinkOutlinedIcon />
              </IconButton>
            )}
          </Stack>
        </TableCell>
      </RoleGuard>
    </TableRow>
  )
}
